import { useCallback } from 'react';

import { useTranslator } from '@picsart/rc/services/localization';
import { NotificationStatuses, NotifierDuration, ToastType, useNotifier } from '@picsart/design-system/components/Notifier';

import type { BATCH_TOOLS } from 'pages/Batch/configs';
import { ALLOWED_LIMIT_FILES, BATCH_ERROR, toasterInfo as batchToasterInfo } from 'pages/Batch/configs';

import type { UPLOAD_ERROR } from 'utils/uploadFiles/config';
import { toasterInfo as uploadsToasterInfo } from 'utils/uploadFiles/config';

import analytics from './analytics';

const useUploadNotification = (activeBatchTool: BATCH_TOOLS | null = null) => {
  const translate = useTranslator();
  const { addNotification } = useNotifier();

  const showUploaderNotification = useCallback(
    (errorType: BATCH_ERROR | UPLOAD_ERROR, status: NotificationStatuses, isBatch = true) => {
      const toasterInfo = isBatch ? batchToasterInfo : uploadsToasterInfo;
      analytics.openPopup(toasterInfo.error[errorType].title, activeBatchTool);
      const { title, text } = toasterInfo.error[errorType];
      const isLimitedErrorType = errorType === BATCH_ERROR.exceededFilesLimit;
      const currentError = isLimitedErrorType ? text.replace('50', '{{number}}') : text;
      addNotification({
        toastType: ToastType.Notification,
        status,
        title: translate(title),
        description: translate(currentError, {
          ...(isLimitedErrorType && { number: ALLOWED_LIMIT_FILES }),
        }),
        duration: NotifierDuration.Notification,
      });
    },
    [activeBatchTool, addNotification, translate],
  );

  const showUploaderError = useCallback(
    (errorType: BATCH_ERROR | UPLOAD_ERROR, isBatch = true) => {
      showUploaderNotification(errorType, NotificationStatuses.Error, isBatch);
    },
    [showUploaderNotification],
  );

  const showUploaderWarning = useCallback(
    (errorType: BATCH_ERROR | UPLOAD_ERROR, isBatch = true) => {
      showUploaderNotification(errorType, NotificationStatuses.Warning, isBatch);
    },
    [showUploaderNotification],
  );

  return { showUploaderError, showUploaderWarning };
};

export default useUploadNotification;
